import { StatusService } from "external-services/status-service";
import BookingsUtilsService from 'app/services/bookingsUtils/bookingsUtils.service';
import PouchActions, {PouchDocType} from "app/actions/pouch.actions";
import BookingLookupService from "external-services/booking-lookup-service";
import {bookingStatusType} from "shared-types/bookingStatusTypes";
import PouchSyncService from 'external-services/pouch-sync-service/index';
import ObjectCopyService from 'external-services/object-copy-service';
import {BookingType} from 'shared-types/SharedTypes';

const NS = 'BookingSummaryComponent';

// requiring html templates so that hot reloading works
const template = require('./bookingSummary.component.html');

export default {
  bindings: {
    bookingsByService: '<',
    serviceViewList: '<',
    bookings: '<',
    currentState: '<',
    loadingState: '<',
    onMiddleTableWidth: '&'
  },
  template,
  controllerAs: 'vm',
  controller
};

// just exported for unit tests
export function controller($scope, $element, $window, unregister) {
  "ngInject";

  const vm = this;
  const MIN_EXPANDER_W = 300;
  const STATIC_W = 100;
  let listeners;

  vm.$onInit = () => {
    vm.totalSeated = 0;
    vm.totalCovers = 0;
    vm.totalBookings = 0;
    vm.servicesCount = _getServicesCount();
    vm.serviceWidthExceedsMaxSpace = false;
    vm.restrictedMiddleTableWidth = null;

    _setServiceColumnWidth();

    /**
     * Public functions
     */
    vm.getSeatedVsCovers = getSeatedVsCovers;

    listeners = [];
    listeners.push($scope.$watch(() => vm.serviceViewList, _updateTotals));
    listeners.push($scope.$watch(() => vm.bookings, _updateTotals));

    /**
     * NBI-4787 disabled temporarily until fixed for not just totals, but bookings & covers values as well
     */
    listeners.push(
      PouchActions.handlePouchSyncedDoctype(PouchDocType.booking, (evt, ids) => handleSingleBookingSync(ids))
    );

    $window.addEventListener('resize', _adjustWidth);
  };

  vm.$onDestroy = () => {
    unregister(listeners);
    $window.removeEventListener('resize', _adjustWidth);
  };

  vm.$onChanges = () => {
    _adjustWidth();
  };


  /**
   * When a pouch synchronises a single booking, we check to see if the id matches and update the booking
   * @param updatedBookingIds
   */
  async function handleSingleBookingSync(updatedBookingIds) {
    if (updatedBookingIds.length === 1) {
      const updatedBookingId = updatedBookingIds[0];
      const originalBooking = ObjectCopyService.angularSafeCopy(vm.bookings.find(b => b._id === updatedBookingId));
      if (!originalBooking || originalBooking.bookingType !== BookingType.Booking) {
        return;
      }
      let newBooking;
      try {
        newBooking = await BookingLookupService.getById(updatedBookingId, originalBooking.time);
      } catch (err) {
        console.warn(NS, `error retrieving booking by id "${updatedBookingId}".`);
        return;
      }

      const bookingIndex = vm.bookings.findIndex(b => b._id === updatedBookingId);
      // const copied = ObjectCopyService.angularSafeCopy(vm.bookingsByService);
      let serviceKey = -1;
      let serviceBookingIndex = -1;
      Object.keys(vm.bookingsByService).some((serviceId) => {
        return vm.bookingsByService[serviceId].bookings.some((booking, bookingIndex) => {
          if (booking._id === updatedBookingId) {
            serviceKey = serviceId;
            serviceBookingIndex = bookingIndex;
            return true;
          }
          return false;
        });
      });
      const bookingsByServiceObj = vm.bookingsByService[serviceKey];
      const containsOnlyQuickChanges = PouchSyncService.containsOnlyQuickChanges(originalBooking, newBooking);
      // todo need add condition of service change
      if (containsOnlyQuickChanges) {
        vm.bookings[bookingIndex] = newBooking;
        if (bookingsByServiceObj) {
          if (newBooking.status.statusType === bookingStatusType.cancelled
            && bookingsByServiceObj && bookingsByServiceObj.bookings[serviceBookingIndex]) {
            vm.bookingsByService[serviceKey] = BookingLookupService.modifyBookingAndCoversCount(
              bookingsByServiceObj, newBooking, serviceBookingIndex, -1
            );
          } else if (originalBooking?.status.statusType === bookingStatusType.cancelled
            && bookingsByServiceObj && bookingsByServiceObj.bookings[serviceBookingIndex]) {
            vm.bookingsByService[serviceKey] = BookingLookupService.modifyBookingAndCoversCount(
              bookingsByServiceObj, newBooking, serviceBookingIndex, +1
            );
          } else if (originalBooking.people !== newBooking.people) {

            bookingsByServiceObj.covers += (newBooking.people - originalBooking.people);
          }
          bookingsByServiceObj.bookings[serviceBookingIndex] = newBooking;
        }
        _updateTotals();
      }
    }
  }

  /**
   * Gets the 'seated/covers' text, returning '0' if covers is empty
   * @param {number/object} seated - number of seated or a 'bookingServiceObj' to use to get the seated amount
   * @param {*} covers - number of covers
   */
  function getSeatedVsCovers(seated, covers) {
    if (!covers) {
      return 0;
    }
    if (typeof seated === 'object') {
      seated = _getSeated(seated);
    }
    return `${seated}/${covers}`;
  }


  /**
   * Private functions below here
   */

  function _adjustWidth() {

    let dynamicWidth = _getActualWidthOfMiddleTable();

    // not using window.innerWidth because viewport must be 1 (breaks when zoomed)
    const docWidth = document.documentElement.clientWidth;

    // limits width to 35% of the screen
    const partialScreenW = docWidth * 0.35;
    if (partialScreenW > MIN_EXPANDER_W && dynamicWidth + STATIC_W > partialScreenW) {
      dynamicWidth = partialScreenW - STATIC_W;
      vm.restrictedMiddleTableWidth = dynamicWidth + 'px';
    } else {
      vm.restrictedMiddleTableWidth = null;
    }
    if (vm.currentState !== vm.loadingState) {
      vm.onMiddleTableWidth({dynamicWidth, staticWidth: dynamicWidth ? STATIC_W : 0});
    }
  }

  /**
   * The max width of the totals is dynamic based on services count
   */
  function _setServiceColumnWidth() {
    const COL_W = 55; // should match css 'max-width' value on '.booking-summary--middle-table-name-cell'
    const TOTAL_W = vm.servicesCount * COL_W;
    vm.serviceWidthExceedsMaxSpace = TOTAL_W > MIN_EXPANDER_W;
  }


  /**
   * Calculates seated covers per service
   */
  function _getSeated(bookingServiceObj) {
    return bookingServiceObj.bookings.reduce((acc, bk) => {
      if (_isBookingSeated(bk)) {
        acc += bk.people;
      }
      return acc;
    }, 0);
  }

  /**
   * Returns the width of the middle table by setting it temporarily to position absolute
   */
  function _getActualWidthOfMiddleTable() {
    const middleTable = $element[0].querySelector(`[data-ref="middleTable"]`);

    if (middleTable) {
      middleTable.style.position = 'absolute';
      const dynamicWidth = middleTable.offsetWidth;
      middleTable.style.position = '';
      return dynamicWidth;
    } else {
      return 0;
    }
  }

  /**
   * Bookings service object is an object, so doesn't have a length, so this
   * function calculates what would be the length.
   */
  function _getServicesCount() {
    if (!vm.bookingsByService) {
      return 0;
    }

    let count = 0;
    for (let prop in vm.bookingsByService) {
      count++;
    }
    return count;
  }

  /**
   * sets the total bookings, seated amd covers properties
   */
  function _updateTotals() {

    if (!vm.serviceViewList || !vm.bookings) {
      return;
    }

    let totalCovers = vm.serviceViewList
      .filter(sv => sv.service && _isPrivateFunEvent(sv.service))
      .reduce((acc, sv) => (acc + sv.service.people), 0);

    let totalBookings = 0;
    let totalSeated = 0;

    vm.bookings.forEach(booking => {

      // if booking doesn't have a table attched to it, then it is a wait list booking
      const isOnWaitList = !booking.tables || booking.tables === null || booking.tables.length === 0;

      if (!isOnWaitList) {
        const serviceIndex = BookingsUtilsService.getServiceFromBooking(booking, vm.serviceViewList);
        const isPrivateFun = serviceIndex !== -1 && _isPrivateFunEvent(vm.serviceViewList[serviceIndex].service);

        if (!_isBookingCancelled(booking)) {
          if (!isPrivateFun) {
            totalCovers += booking.people;
            if (_isBookingSeated(booking)) {
              totalSeated += booking.people;
            }
          }
          totalBookings += 1;
        }
      }

    });

    vm.totalCovers = totalCovers;
    vm.totalSeated = totalSeated;
    vm.totalBookings = totalBookings;
  }

  function _isBookingCancelled(booking) {
    if (!booking.status) {
      StatusService.populateMissingStatusInBooking(booking);
      return false;
    }
    return booking.status.statusType === 'cancelled';
  }

  function _isBookingSeated(booking) {
    if (!booking.status) {
      StatusService.populateMissingStatusInBooking(booking);
      return false;
    }
    return booking.status.statusType === 'seated';
  }

  function _isPrivateFunEvent(service) {
    return service && service.type === 'event' && service.eventType === 'PrivateFunction';
  }
}
